import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-date-picker";
import { useIntl } from "react-intl";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
} from "reactstrap";
import { useFacebookCore } from "../../../cores/facebook";
import { useInstagramCore } from "../../../cores/instagram";
import { useLinkedInCore } from "../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../cores/linkedInBusiness";
import { useMeCore } from "../../../cores/me";
import { useSocialPostCore } from "../../../cores/socialPost";
import { SocialProviderId } from "../../../cores/socialPost/config";
import {
  SmartTime,
  SMART_TIMES,
} from "../../../cores/socialPost/consts/smartTimes.constant";
import { SOCIAL_ACTION_TYPES } from "../../../cores/socialPost/consts/socialActionType.constant";
import { SOCIAL_POST_TYPES } from "../../../cores/socialPost/consts/socialPostType.constant";
import { SocialType } from "../../../cores/socialPost/interfaces/postError";
import { useTwitterCore } from "../../../cores/twitter";
import { LabelWembii } from "../LabelWembii";
import { scheduleDateStringForApiFromDateAndHour } from "../../../cores/socialPost/helpers/scheduleDateForApi";
import cn from "classnames";

interface Props {
  onClose?: () => void;
  onReset?: () => void;
  socialType: SocialType;
  repost: boolean;
}

export const PostForm = (props: Props) => {
  const { onClose, onReset, socialType, repost } = props;
  const [date, setDate] = useState<Date>(
    moment().startOf("day").add(12, "hours").toDate()
  );

  const [hour, setHour] = useState<string>(moment().format("HH:mm"));
  const [showAllHours, setShowAllHours] = useState<boolean>(false);
  /** Horario inteligente: colapsado por defecto */
  const [smartScheduleOpen, setSmartScheduleOpen] = useState(false);
  const timeInputRef = useRef<HTMLInputElement>(null);

  const openNativeTimePicker = useCallback(() => {
    const el = timeInputRef.current;
    if (!el) return;
    try {
      const anyEl = el as HTMLInputElement & { showPicker?: () => void };
      if (typeof anyEl.showPicker === "function") {
        anyEl.showPicker();
        return;
      }
    } catch {
      /* showPicker puede fallar si no hay gesto de usuario */
    }
    el.focus();
    el.click();
  }, []);
  const {
    images,
    video,
    text = "",
    mentions,
    socialProviders,
    selectedPost,
    setSocialAction,
  } = useSocialPostCore();
  const {
    socialPost: socialPostTwitter,
    socialPostVideo: socialPostVideoTwitter,
    modifySocialPost: modifySocialPostTwitter,
    modifySocialPostVideo: modifySocialPostVideoTwitter,
  } = useTwitterCore();
  const {
    socialPost: socialPostFacebook,
    socialPostVideo: socialPostVideoFacebook,
    modifySocialPost: modifySocialPostFacebook,
    modifySocialPostVideo: modifySocialPostVideoFacebook,
    socialStory: socialStoryFacebook,
    socialStoryVideo: socialStoryVideoFacebook,
    modifySocialStory: modifySocialStoryFacebook,
    modifySocialStoryVideo: modifySocialStoryVideoFacebook,
  } = useFacebookCore();
  const {
    socialPost: socialPostLinkedIn,
    modifySocialPost: modifySocialPostLinkedIn,
  } = useLinkedInCore();
  const {
    socialPost: socialPostLinkedInBusiness,
    socialPostVideo: socialPostVideoLinkedInBusiness,
    modifySocialPost: modifySocialPostLinkedInBusiness,
    modifySocialPostVideo: modifySocialPostVideoLinkedInBusiness,
  } = useLinkedInBusinessCore();
  const {
    socialPost: socialPostInstagram,
    socialPostVideo: socialPostVideoInstagram,
    modifySocialPost: modifySocialPostInstagram,
    modifySocialPostVideo: modifySocialPostVideoInstagram,
    socialStory: socialStoryInstagram,
    socialStoryVideo: socialStoryVideoInstagram,
    modifySocialStory: modifySocialStoryInstagram,
    modifySocialStoryVideo: modifySocialStoryVideoInstagram,
    cleanErrors,
  } = useInstagramCore();
  const { company } = useMeCore();
  const intl = useIntl();

  const instagramScheduleOk = useMemo(() => {
    const hasInstagram =
      socialProviders.find((sp) => sp.id === SocialProviderId.Instagram) != null;
    if (!hasInstagram) return true;
    // Requisito: Instagram solo se agenda con al menos 1 imagen.
    return images.length > 0;
  }, [socialProviders, images.length]);

  const disabled = useMemo(
    () => !date || !hour || !instagramScheduleOk,
    [date, hour, instagramScheduleOk]
  );

  useEffect(() => {
    if (selectedPost?.post.scheduleDate) {
      const m = moment(selectedPost?.post.scheduleDate);
      setDate(m.startOf("day").add(12, "hours").toDate());
      setHour(moment(selectedPost?.post.scheduleDate).format("HH:mm"));
    } else {
      setDate(moment().startOf("day").add(12, "hours").toDate());
      setHour(moment().format("HH:mm"));
    }
  }, [selectedPost]);

  const onSuccess = useCallback(() => {
    onReset?.();
    cleanErrors();
  }, [onReset, cleanErrors]);

  const onSubmitPost = useCallback(() => {
    setSocialAction(SOCIAL_ACTION_TYPES.SCHEDULE);
    const parseDate =
      date && hour
        ? scheduleDateStringForApiFromDateAndHour(date, hour)
        : "";

    const repostId = repost ? selectedPost?.post.id! : "";

    socialProviders.forEach((sp) => {
      switch (sp.id) {
        case SocialProviderId.Twitter:
          video ||
          (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0)
            ? !selectedPost || repost
              ? socialPostVideoTwitter(
                  company?.id!,
                  text,
                  mentions,
                  video!,
                  parseDate,
                  repostId,
                  onSuccess
                )
              : modifySocialPostVideoTwitter(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  parseDate,
                  selectedPost.media,
                  onSuccess
                )
            : !selectedPost || repost
            ? socialPostTwitter(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              )
            : modifySocialPostTwitter(
                company?.id!,
                selectedPost.post.id,
                text,
                mentions,
                images,
                parseDate,
                selectedPost.media,
                onSuccess
              );
          break;
        case SocialProviderId.LinkedInBusiness:
          video ||
          (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0)
            ? !selectedPost || repost
              ? socialPostVideoLinkedInBusiness(
                  company?.id!,
                  text,
                  mentions,
                  video!,
                  parseDate,
                  repostId,
                  onSuccess
                )
              : modifySocialPostVideoLinkedInBusiness(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  parseDate,
                  selectedPost.media,
                  onSuccess
                )
            : !selectedPost || repost
            ? socialPostLinkedInBusiness(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              )
            : modifySocialPostLinkedInBusiness(
                company?.id!,
                selectedPost.post.id,
                text,
                mentions,
                images,
                parseDate,
                selectedPost.media,
                onSuccess
              );
          break;
        case SocialProviderId.LinkedIn:
          !selectedPost || repost
            ? socialPostLinkedIn(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              )
            : modifySocialPostLinkedIn(
                company?.id!,
                selectedPost.post.id,
                text,
                mentions,
                images,
                parseDate,
                selectedPost.media,
                onSuccess
              );
          break;
        case SocialProviderId.Facebook:
          video ||
          (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0)
            ? !selectedPost || repost
              ? socialPostVideoFacebook(
                  company?.id!,
                  text,
                  mentions,
                  video!,
                  parseDate,
                  repostId,
                  onSuccess
                )
              : modifySocialPostVideoFacebook(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  parseDate,
                  selectedPost.media,
                  onSuccess
                )
            : !selectedPost || repost
            ? socialPostFacebook(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              )
            : modifySocialPostFacebook(
                company?.id!,
                selectedPost.post.id,
                text,
                mentions,
                images,
                parseDate,
                selectedPost.media,
                onSuccess
              );
          break;
        case SocialProviderId.Instagram:
          video ||
          (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0)
            ? !selectedPost || repost
              ? socialPostVideoInstagram(
                  company?.id!,
                  text,
                  mentions,
                  video!,
                  parseDate,
                  repostId,
                  onSuccess
                )
              : modifySocialPostVideoInstagram(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  mentions,
                  video,
                  parseDate,
                  selectedPost.media,
                  onSuccess
                )
            : !selectedPost || repost
            ? socialPostInstagram(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              )
            : modifySocialPostInstagram(
                company?.id!,
                selectedPost.post.id,
                text,
                mentions,
                images,
                parseDate,
                selectedPost.media,
                onSuccess
              );
          break;
        default:
          console.log(`No hay red social para programar ${sp.name}`);
          break;
      }
    });
    onClose?.();
  }, [
    date,
    hour,
    socialProviders,
    video,
    company,
    text,
    images,
    mentions,
    repost,
    onClose,
    socialPostVideoTwitter,
    socialPostTwitter,
    socialPostVideoLinkedInBusiness,
    socialPostLinkedInBusiness,
    socialPostLinkedIn,
    socialPostVideoFacebook,
    socialPostFacebook,
    socialPostVideoInstagram,
    socialPostInstagram,
    modifySocialPostFacebook,
    modifySocialPostInstagram,
    modifySocialPostLinkedIn,
    modifySocialPostLinkedInBusiness,
    modifySocialPostTwitter,
    modifySocialPostVideoFacebook,
    modifySocialPostVideoInstagram,
    modifySocialPostVideoLinkedInBusiness,
    modifySocialPostVideoTwitter,
    onSuccess,
    setSocialAction,
    selectedPost,
  ]);

  const onSubmitStory = useCallback(() => {
    setSocialAction(SOCIAL_ACTION_TYPES.SCHEDULE);
    const parseDate =
      date && hour
        ? scheduleDateStringForApiFromDateAndHour(date, hour)
        : "";

    socialProviders.forEach((sp) => {
      switch (sp.id) {
        case SocialProviderId.Instagram:
          video ||
          (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0)
            ? !selectedPost
              ? socialStoryVideoInstagram(
                  company?.id!,
                  text,
                  video!,
                  parseDate,
                  onReset
                )
              : modifySocialStoryVideoInstagram(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  video,
                  parseDate,
                  selectedPost.media,
                  onReset
                )
            : !selectedPost
            ? socialStoryInstagram(
                company?.id!,
                text,
                images,
                parseDate,
                onReset
              )
            : modifySocialStoryInstagram(
                company?.id!,
                selectedPost.post.id,
                text,
                images,
                parseDate,
                selectedPost.media,
                onReset
              );
          break;
        case SocialProviderId.Facebook:
          video ||
          (selectedPost?.post.type === SOCIAL_POST_TYPES.VIDEO &&
            selectedPost.media.length > 0)
            ? !selectedPost
              ? socialStoryVideoFacebook(
                  company?.id!,
                  text,
                  video!,
                  parseDate,
                  onReset
                )
              : modifySocialStoryVideoFacebook(
                  company?.id!,
                  selectedPost.post.id,
                  text,
                  video,
                  parseDate,
                  selectedPost.media,
                  onReset
                )
            : !selectedPost
            ? socialStoryFacebook(
                company?.id!,
                text,
                images,
                parseDate,
                onReset
              )
            : modifySocialStoryFacebook(
                company?.id!,
                selectedPost.post.id,
                text,
                images,
                parseDate,
                selectedPost.media,
                onReset
              );
          break;
        default:
          break;
      }
    });
    onClose?.();
  }, [
    socialProviders,
    video,
    company,
    text,
    images,
    date,
    hour,
    selectedPost,
    onClose,
    socialStoryFacebook,
    socialStoryInstagram,
    socialStoryVideoFacebook,
    socialStoryVideoInstagram,
    onReset,
    setSocialAction,
    modifySocialStoryFacebook,
    modifySocialStoryInstagram,
    modifySocialStoryVideoFacebook,
    modifySocialStoryVideoInstagram,
  ]);

  const getSmartTimes = useCallback(() => {
    return SMART_TIMES[`${moment(date).isoWeekday() - 1}`];
  }, [date]);

  const onSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      console.log({ isTrue: socialType === SocialType.Post });
      if (socialType === SocialType.Post) onSubmitPost();
      else onSubmitStory();
    },
    [onSubmitPost, onSubmitStory, socialType]
  );

  const formatSmartTime = (time: SmartTime) =>
    moment(time.hour + ":" + time.minutes, "H:mm").format("HH:mm");

  // FunciÃ³n para calcular el porcentaje de engagement basado en el score
  const getEngagementPercentage = (score: number): string => {
    const percentages = {
      1: "+8%",
      2: "+14%", 
      3: "+21%",
      4: "+27%",
      5: "+32%"
    };
    return percentages[score as keyof typeof percentages] || "+8%";
  };

  // FunciÃ³n para obtener los mejores horarios ordenados por score
  const getTopSmartTimes = () => {
    const times = getSmartTimes();
    return times
      .sort((a, b) => b.score - a.score)
      .slice(0, 5); // Top 5 mejores horarios
  };

  // FunciÃ³n para obtener todos los horarios ordenados por hora
  const getAllSmartTimes = () => {
    const times = getSmartTimes();
    return times.sort((a, b) => {
      const timeA = a.hour * 60 + a.minutes;
      const timeB = b.hour * 60 + b.minutes;
      return timeA - timeB;
    });
  };

  // FunciÃ³n para obtener los horarios a mostrar
  const getDisplayTimes = () => {
    return showAllHours ? getAllSmartTimes() : getTopSmartTimes();
  };

  return (
    <Container className="h-100" fluid>
      <Row>
        <Col xs="12">
          <div className="d-flex flex-column h-100 p-2 py-3">
            <h2 className="h2 text-center mb-4">
              {intl.formatMessage({ id: "app.programModalPost.title" })}
            </h2>
            <Form className="p-2 px-4" onSubmit={onSubmit}>
              {/* SecciÃ³n de Fecha Mejorada */}
              <div className="date-section mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <LabelWembii>
                    {intl.formatMessage({ id: "app.programModalPost.Date" })}
                  </LabelWembii>
                  <div className="best-day-indicator">
                    <FontAwesomeIcon icon={["fas", "info-circle"]} color="#0B65C2" className="me-1" />
                    <span className="best-day-text">
                      {intl.formatMessage({ id: "app.programModalPost.bestDay" })}: {moment(date).format('dddd')}
                    </span>
                  </div>
                </div>
                <div className="date-picker-container">
                  <DatePicker
                    className="w-100"
                    format="dd/MM/y"
                    value={date}
                    clearIcon={null}
                    onChange={(v: any) => {
                      if (!v) return;
                      setDate(v);
                    }}
                  />
                  <div className="calendar-icon">
                    <FontAwesomeIcon icon={["far", "calendar"]} color="#0B65C2" />
                  </div>
                </div>
              </div>
              {/* SecciÃ³n de Hora Mejorada */}
              <div className="time-section mb-4">
                <LabelWembii>
                  {intl.formatMessage({ id: "app.programModalPost.time" })}
                </LabelWembii>
                <div
                  className="time-picker-container"
                  role="presentation"
                  onClick={(e) => {
                    if (e.target === timeInputRef.current) return;
                    openNativeTimePicker();
                  }}
                >
                  <input
                    ref={timeInputRef}
                    type="time"
                    id="exampleTime"
                    name="time"
                    className="time-native-input w-100"
                    aria-label={intl.formatMessage({
                      id: "app.programModalPost.time",
                    })}
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    onClick={() => openNativeTimePicker()}
                  />
                  <div className="clock-icon" aria-hidden>
                    <FontAwesomeIcon icon={["far", "clock"]} color="#0B65C2" />
                  </div>
                </div>
              </div>

              {/* Horario inteligente: colapsado por defecto */}
              <div
                className={cn("smart-schedule-section mt-4", {
                  "smart-schedule-section--collapsed": !smartScheduleOpen,
                })}
              >
                <button
                  type="button"
                  id="smart-schedule-heading"
                  className="smart-schedule-header-toggle"
                  onClick={() => setSmartScheduleOpen((o) => !o)}
                  aria-expanded={smartScheduleOpen}
                  aria-controls="smart-schedule-panel"
                >
                  <span className="d-flex align-items-center min-width-0">
                    <span className="smart-icon me-2">
                      <FontAwesomeIcon icon={["fas", "chart-bar"]} color="#0B65C2" />
                    </span>
                    <span className="smart-title text-left">
                      {intl.formatMessage({
                        id: "app.programModalPost.SmartTime",
                      })}
                    </span>
                  </span>
                  <FontAwesomeIcon
                    icon={["fas", smartScheduleOpen ? "chevron-up" : "chevron-down"]}
                    color="#6b7280"
                    className="smart-schedule-header-toggle__chevron"
                  />
                </button>

                {smartScheduleOpen && (
                  <div
                    id="smart-schedule-panel"
                    role="region"
                    aria-labelledby="smart-schedule-heading"
                  >
                    <div className="smart-info mb-3 mt-2">
                      <FontAwesomeIcon
                        icon={["fas", "info-circle"]}
                        color="#0B65C2"
                        className="me-2 flex-shrink-0"
                      />
                      <span className="smart-description">
                        {intl.formatMessage({
                          id: "app.programModalPost.smartDescription",
                        })}
                      </span>
                    </div>

                    <div className="smart-times-table">
                      <div className="table-header">
                        <div className="header-cell time-header">
                          {intl.formatMessage({
                            id: "app.programModalPost.recommendedTimes",
                          })}
                        </div>
                        <div className="header-cell engagement-header">
                          {intl.formatMessage({
                            id: "app.programModalPost.engagement",
                          })}
                        </div>
                      </div>

                      <div
                        className={`times-container ${showAllHours ? "expanded" : "collapsed"}`}
                      >
                        {getDisplayTimes().map((time, index) => (
                          <div
                            key={`smart-time-${index}`}
                            className={`table-row ${hour === formatSmartTime(time) ? "selected" : ""}`}
                            onClick={() => setHour(formatSmartTime(time))}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setHour(formatSmartTime(time));
                              }
                            }}
                          >
                            <div className="table-cell time-cell">
                              {moment(
                                time.hour + ":" + time.minutes,
                                "H:mm"
                              ).format("hh:mm A")}
                            </div>
                            <div className="table-cell engagement-cell">
                              <div className="engagement-content">
                                <span className="engagement-percentage">
                                  {getEngagementPercentage(time.score)}
                                </span>
                                <div className="stars-container">
                                  {_.range(5).map((item) => (
                                    <FontAwesomeIcon
                                      key={`star${time.hour}${item}`}
                                      icon={
                                        item < time.score
                                          ? ["fas", "star"]
                                          : ["far", "star"]
                                      }
                                      size="sm"
                                      color={
                                        item < time.score ? "#FFD700" : "#E5E5E5"
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="expand-button-container">
                        <button
                          type="button"
                          className="expand-button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAllHours(!showAllHours);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              showAllHours
                                ? ["fas", "chevron-up"]
                                : ["fas", "chevron-down"]
                            }
                            className="me-2"
                          />
                          {showAllHours
                            ? intl.formatMessage({
                                id: "app.programModalPost.showLess",
                              })
                            : intl.formatMessage({
                                id: "app.programModalPost.showAllHours",
                              })}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Botones de AcciÃ³n Mejorados */}
              <div className="action-buttons mt-5">
                <div className="row g-3">
                  <div className="col-6">
                    <Button
                      className="cancel-button w-100"
                      color="secondary"
                      outline
                      onClick={onClose}
                    >
                      <FontAwesomeIcon icon={["fas", "times"]} className="me-2" />
                      {intl.formatMessage({ id: "app.socialMediaPost.cancel" })}
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button
                      className="schedule-button w-100"
                      color="primary"
                      disabled={disabled}
                      type="submit"
                    >
                      <FontAwesomeIcon icon={["far", "calendar"]} className="me-2" />
                      {intl.formatMessage({ id: "app.programModalPost.title" })}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};
